import { FilterUpdateEvent, ThemeEvents } from '@theme/events';

const AVAILABILITY_PARAM = 'filter.v.availability';
const IN_STOCK = '1';
const DEFAULT_SORT = 'created-descending';
const DEFAULT_GRID = 'default';

const isCollectionPage = () => String(window.Theme?.template?.name || '').startsWith('collection');

const hideOosEnabled = () =>
  Boolean(
    document.body?.hasAttribute('data-hide-oos-collections') ||
      document.body?.classList.contains('hide-oos-collections') ||
      window.Theme?.hideOutOfStockFromCollections
  );

const isActive = () => isCollectionPage() && hideOosEnabled();

/**
 * @param {URLSearchParams} params
 * @param {{ fillSortAndGrid?: boolean }} [options]
 */
function lockInStockParams(params, { fillSortAndGrid = false } = {}) {
  params.delete(AVAILABILITY_PARAM);
  params.append(AVAILABILITY_PARAM, IN_STOCK);

  if (!fillSortAndGrid) return params;

  const current = new URL(window.location.href).searchParams;
  if (!params.get('sort_by')) {
    if (current.get('sort_by')) params.set('sort_by', current.get('sort_by'));
  }
  if (!params.get('grid')) {
    params.set('grid', current.get('grid') || DEFAULT_GRID);
  }

  return params;
}

/**
 * @param {string} urlString
 * @returns {string}
 */
function lockInStockUrl(urlString) {
  const url = new URL(urlString, window.location.origin);
  lockInStockParams(url.searchParams, { fillSortAndGrid: true });
  return `${url.pathname}${url.search}${url.hash}`;
}

function lockAvailabilityInputs(root = document) {
  root.querySelectorAll(`input[name="${AVAILABILITY_PARAM}"]`).forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    if (input.value === IN_STOCK) {
      input.checked = true;
      input.setAttribute('checked', '');
    } else {
      input.checked = false;
      input.removeAttribute('checked');
    }
  });

  root.querySelectorAll('facet-remove-component[data-url]').forEach((el) => {
    const isAvailabilityChip =
      el.classList.contains('facets-remove__pill') || el.classList.contains('pills__pill');
    if (!isAvailabilityChip || el.querySelector('.facets__clear-all-link')) {
      el.removeAttribute('data-locked-availability');
      return;
    }

    const removeUrl = el.getAttribute('data-url') || '';
    try {
      const parsed = new URL(removeUrl, window.location.origin);
      const removesAvailability = !parsed.searchParams.getAll(AVAILABILITY_PARAM).includes(IN_STOCK);
      const currentHasAvailability = new URL(window.location.href).searchParams
        .getAll(AVAILABILITY_PARAM)
        .includes(IN_STOCK);

      if (removesAvailability && currentHasAvailability) {
        el.setAttribute('data-locked-availability', '');
      } else {
        el.removeAttribute('data-locked-availability');
      }
    } catch {
      // Ignore malformed URLs
    }
  });
}

function patchFacetsForm() {
  customElements.whenDefined('facets-form-component').then(() => {
    const FacetsFormComponent = customElements.get('facets-form-component');
    if (!FacetsFormComponent || FacetsFormComponent.prototype.__inStockLocked) return;

    const originalCreateURLParameters = FacetsFormComponent.prototype.createURLParameters;
    FacetsFormComponent.prototype.createURLParameters = function createURLParameters(formData) {
      const params = originalCreateURLParameters.call(this, formData);
      if (isActive()) lockInStockParams(params);
      return params;
    };

    const originalUpdateFiltersByURL = FacetsFormComponent.prototype.updateFiltersByURL;
    FacetsFormComponent.prototype.updateFiltersByURL = function updateFiltersByURL(url) {
      if (isActive() && url) {
        originalUpdateFiltersByURL.call(this, lockInStockUrl(url));
        return;
      }
      originalUpdateFiltersByURL.call(this, url);
    };

    FacetsFormComponent.prototype.__inStockLocked = true;
  });
}

function patchClearAllVisibility() {
  const originalShouldShowClearAll = FilterUpdateEvent.prototype.shouldShowClearAll;
  if (FilterUpdateEvent.prototype.__inStockLocked) return;

  FilterUpdateEvent.prototype.shouldShowClearAll = function shouldShowClearAll() {
    if (!isActive()) return originalShouldShowClearAll.call(this);

    return [...this.detail.queryParams.entries()].filter(([key, value]) => {
      if (key === AVAILABILITY_PARAM && value === IN_STOCK) return false;
      return key.startsWith('filter.');
    }).length > 0;
  };

  FilterUpdateEvent.prototype.__inStockLocked = true;
}

function preventAvailabilityToggle(event) {
  if (!isActive()) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const input = target.closest(`input[name="${AVAILABILITY_PARAM}"]`);
  if (input instanceof HTMLInputElement) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (input.value === IN_STOCK) {
      input.checked = true;
    } else {
      input.checked = false;
    }
  }

  const removeButton = target.closest('facet-remove-component[data-locked-availability]');
  if (removeButton) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}

function reflowFilterOverflow() {
  document.querySelectorAll('overflow-list.facets__overflow-list').forEach((list) => {
    list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
  });
}

function init() {
  if (!isActive()) return;

  patchFacetsForm();
  patchClearAllVisibility();
  lockAvailabilityInputs();
  requestAnimationFrame(reflowFilterOverflow);

  document.addEventListener('click', preventAvailabilityToggle, true);
  document.addEventListener('change', preventAvailabilityToggle, true);
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') preventAvailabilityToggle(event);
    },
    true
  );

  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    queueMicrotask(() => {
      lockAvailabilityInputs();
      reflowFilterOverflow();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
